import { motion } from "framer-motion";
import { ShoppingBag, Store, Globe, Boxes, Package, Truck, Radar, CheckCircle2, ShoppingCart, Zap } from "lucide-react";

const FLOW = [
  { icon: ShoppingCart, label: "Store Order" },
  { icon: Zap, label: "ShipAny" },
  { icon: Boxes, label: "Courier Selection" },
  { icon: Truck, label: "Pickup" },
  { icon: Radar, label: "Tracking" },
  { icon: CheckCircle2, label: "Delivery" },
];
const INTEGRATIONS = [
  { id: "shopify", name: "Shopify", icon: ShoppingBag, tint: "#10B981" },
  { id: "woocommerce", name: "WooCommerce", icon: Store, tint: "#7F54B3" },
  { id: "amazon", name: "Amazon", icon: Package, tint: "#FF9933" },
  { id: "flipkart", name: "Flipkart", icon: ShoppingCart, tint: "#12C8F5" },
  { id: "meesho", name: "Meesho", icon: Store, tint: "#F43F5E" },
  { id: "own-website", name: "Own Website", icon: Globe, tint: "#0757D9" },
];

export default function D2CSection() {
  return (
    <section className="relative overflow-hidden bg-[#F7FAFC] py-20 text-shipany-navy sm:py-24" data-testid="d2c-section">
      <div className="pointer-events-none absolute -left-24 top-16 h-72 w-72 rounded-full bg-[#FF9933]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-[#10B981]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#0757D9]">D2C Shipping</span>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Built for India's Fast-Growing D2C Brands</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500 sm:text-base">From store order to doorstep delivery — one automated shipping pipeline.</p>
        </motion.div>

        {/* order flow */}
        <div className="mt-12 flex flex-wrap items-stretch justify-center gap-2 sm:gap-3" data-testid="d2c-order-flow">
          {FLOW.map((s, i) => (
            <motion.div key={s.label} className="flex items-center gap-2 sm:gap-3"
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.09, duration: 0.45 }}>
              <div className={`flex flex-col items-center rounded-2xl border px-4 py-3.5 shadow-sm transition-all duration-300 hover:-translate-y-1 ${
                s.label === "ShipAny" ? "border-[#FF9933]/50 bg-white shadow-[0_10px_30px_rgba(255,153,51,0.18)]" : "border-slate-200 bg-white"}`}>
                <s.icon className={`h-5 w-5 ${s.label === "ShipAny" ? "text-[#FF9933]" : "text-[#0757D9]"}`} />
                <span className="mt-1.5 whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-slate-600 sm:text-[11px]">{s.label}</span>
              </div>
              {i < FLOW.length - 1 && (
                <svg width="26" height="10" viewBox="0 0 26 10" className="hidden shrink-0 sm:block">
                  <line x1="0" y1="5" x2="22" y2="5" stroke="#0757D9" strokeWidth="1.6" className="route-dash-fast" />
                  <path d="M 20 1 L 26 5 L 20 9" fill="none" stroke="#0757D9" strokeWidth="1.6" />
                </svg>
              )}
            </motion.div>
          ))}
        </div>

        {/* integrations */}
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {INTEGRATIONS.map((g, i) => (
            <motion.div key={g.id} data-testid={`d2c-integration-${g.id}`}
              initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.4 }}
              whileHover={{ y: -5, rotate: -1 }}
              className="group flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-lg">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${g.tint}16`, boxShadow: `inset 0 0 0 1px ${g.tint}30` }}>
                <g.icon className="h-5 w-5" style={{ color: g.tint }} />
              </span>
              <span className="mt-3 text-xs font-bold text-slate-700">{g.name}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
